import { useEffect, useState } from 'react';
import { BookingOption, PROMOTION_BASE_TIER, formatEuro } from '@radevents/shared';
import bookingService from '../../../services/bookingService';

export interface BookingOffer {
  /**
   * Was Premium diesen Veranstalter kostet — geholt, nicht angenommen.
   *
   * Der Preis steht in `price_items` und hängt an der Einordnung (Verein/kommerziell); ein fest
   * verdrahteter Betrag wäre beim ersten Preisexperiment falsch, und zwar an der Stelle, an der
   * jemand kauft. Zwei Preise heißt: dieser Veranstalter ist noch nicht eingeordnet und wählt im
   * Dialog selbst — dann ist „ab" die einzige ehrliche Angabe.
   */
  premiumPrice: string | null;
  /**
   * Was die Hervorhebung kostet (Netto-Cent des Rahmens bis zur Veranstaltung), oder `null`.
   *
   * Ohne Event kennt der Server keine Laufzeit und meldet jede Hervorhebung als „gilt für ein
   * einzelnes Event" — die Preise stehen trotzdem in der Antwort. Ein laufendes Premium ist
   * darin schon eingerechnet, weil der Server den Rabatt in die Beträge rechnet.
   */
  promotionFrom: number | null;
  /**
   * Der Steuersatz dieser Preise — für „zzgl. 19 % MwSt." neben dem Betrag.
   *
   * Steht dabei, weil die Beträge netto sind (V159): ein Nettopreis ohne Steuerangabe wäre eine
   * falsche Zahl. `null`, solange nichts geladen ist; dann steht auch kein Preis da.
   */
  vatPercent: number | null;
}

const EMPTY: BookingOffer = { premiumPrice: null, promotionFrom: null, vatPercent: null };

/**
 * Der günstigste Nettopreis unter diesen Posten — `null`, wenn es keine gibt.
 *
 * Netto, weil der Veranstalter-Bereich Preise so anzeigt wie das Angebot (V159); der zu zahlende
 * Bruttobetrag steht im Checkout und in der Bestellung.
 */
const cheapest = (options: BookingOption[]): number | null =>
  options.length === 0 ? null : Math.min(...options.map(o => o.netCents));

/** Glatte Beträge ohne Nachkommastellen: „199 € im Jahr" ist ein Preis, „199,00 €" ein Posten. */
const premiumPriceOf = (options: BookingOption[]): string | null => {
  const premium = options.filter(o => o.kind === 'VERIFICATION' && o.available);
  if (premium.length === 0) return null;
  const cheapest = Math.min(...premium.map(o => o.netCents));
  const amount = cheapest % 100 === 0
    ? `${(cheapest / 100).toLocaleString('de-DE')} €`
    : formatEuro(cheapest);
  return `${premium.length > 1 ? 'ab ' : ''}${amount} im Jahr`;
};

/**
 * Was dieser Veranstalter gerade bestellen kann — Premium-Preis und Einstiegspreis der
 * Hervorhebung aus **einer** Abfrage (`/booking-options`).
 *
 * Die redaktionellen Beiträge (`SOCIAL_POST`, `NEWS_ARTICLE`) sind seit dem 11.09.2026 kein
 * Katalogposten mehr: sie lagen seit V134 abgeschaltet und mit 0 € in `price_items`, und ein
 * Angebot, das „derzeit nicht buchbar" meldet, kostet mehr Vertrauen als eine fehlende Zeile.
 * An ihrer Stelle steht im Reiter der Weg zum Gespräch (`docs/monetarisierung.md`).
 */
export const useBookingOffer = (organizerId: number): BookingOffer => {
  const [offer, setOffer] = useState<BookingOffer>(EMPTY);

  useEffect(() => {
    if (!organizerId) return;
    let alive = true;
    setOffer(EMPTY);
    bookingService.options(organizerId)
      .then(options => {
        if (!alive) return;
        const base = options.filter(o => o.kind === 'PROMOTION' && o.promoTier === PROMOTION_BASE_TIER);
        setOffer({
          premiumPrice: premiumPriceOf(options),
          promotionFrom: cheapest(base.length ? base : options.filter(o => o.kind === 'PROMOTION')),
          vatPercent: options[0]?.vatPercent ?? null,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [organizerId]);

  return offer;
};

export default useBookingOffer;
